import React, { useState, useEffect } from 'react';
import { trainerService } from '../../services/authService';
import './TrainerDashboard.css';

const TrainerDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const data = await trainerService.getTrainerDashboard();
            setDashboardData(data);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch dashboard data');
            console.error('Dashboard fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="spinner"></div>
                <p>Loading your dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <div className="error-content">
                    <i className="error-icon fas fa-exclamation-circle"></i>
                    <h2>Error Loading Dashboard</h2>
                    <p>{error}</p>
                    <div className="error-actions">
                        <button className="retry-button" onClick={fetchDashboardData}>
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="trainer-dashboard">
            {/* Profile Section */}
            <div className="dashboard-header">
                <div className="profile-summary">
                    <h1>Welcome, {dashboardData.profile.name}</h1>
                    <p className="trainer-info">
                        <span>{dashboardData.profile.expertise}</span> • 
                        <span>{dashboardData.profile.subject_taught}</span>
                    </p>
                </div>
                <div className="dashboard-tabs">
                    <button 
                        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => handleTabChange('overview')}
                    >
                        Overview
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'courses' ? 'active' : ''}`}
                        onClick={() => handleTabChange('courses')}
                    >
                        Courses
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
                        onClick={() => handleTabChange('students')}
                    >
                        Students
                    </button>
                </div>
            </div>

            {activeTab === 'overview' && (
                <>
                    {/* Statistics Section */}
                    <div className="stats-grid">
                        <div className="stat-card">
                            <h3>Total Students</h3>
                            <p className="stat-number">{dashboardData.stats.totalStudents}</p>
                            <div className="stat-trend">
                                <span className={`trend-indicator ${dashboardData.stats.studentTrend > 0 ? 'positive' : 'negative'}`}>
                                    <i className={`fas fa-arrow-${dashboardData.stats.studentTrend > 0 ? 'up' : 'down'}`}></i>
                                    {Math.abs(dashboardData.stats.studentTrend)}%
                                </span>
                                <span className="trend-period">vs last month</span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <h3>Active Courses</h3>
                            <p className="stat-number">{dashboardData.stats.activeCourses}</p>
                            <div className="stat-trend">
                                <span className="trend-indicator neutral">
                                    <i className="fas fa-minus"></i>
                                    No change
                                </span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <h3>Completed Courses</h3>
                            <p className="stat-number">{dashboardData.stats.completedCourses}</p>
                            <div className="stat-trend">
                                <span className={`trend-indicator ${dashboardData.stats.courseTrend > 0 ? 'positive' : 'negative'}`}>
                                    <i className={`fas fa-arrow-${dashboardData.stats.courseTrend > 0 ? 'up' : 'down'}`}></i>
                                    {Math.abs(dashboardData.stats.courseTrend)}%
                                </span>
                                <span className="trend-period">vs last month</span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <h3>Average Rating</h3>
                            <p className="stat-number">{dashboardData.stats.averageRating.toFixed(1)}</p>
                            <div className="rating-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <i 
                                        key={star}
                                        className={`fas fa-star ${star <= dashboardData.stats.averageRating ? 'filled' : ''}`}
                                    ></i>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Recent Activities Section */}
                    <div className="dashboard-section">
                        <h2>Recent Activities</h2>
                        <div className="activities-list">
                            {dashboardData.recentActivities.map(activity => (
                                <div key={activity.id} className="activity-card">
                                    <div className="activity-icon">
                                        <i className={`fas fa-${getActivityIcon(activity.type)}`}></i>
                                    </div>
                                    <div className="activity-details">
                                        <p>{activity.description}</p>
                                        <div className="activity-meta">
                                            <span className="activity-date">
                                                {new Date(activity.date).toLocaleDateString()}
                                            </span>
                                            {activity.course && (
                                                <span className="activity-course">
                                                    <i className="fas fa-book"></i>
                                                    {activity.course}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'courses' && (
                <div className="dashboard-section">
                    <div className="section-header">
                        <h2>Your Courses</h2>
                        <button className="add-course-btn">
                            <i className="fas fa-plus"></i>
                            Add New Course
                        </button>
                    </div>
                    <div className="courses-grid">
                        {dashboardData.courses.map(course => (
                            <div key={course.id} className="course-card">
                                <div className="course-header">
                                    <h3>{course.title}</h3>
                                    <div className="course-actions">
                                        <button className="action-btn edit">
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button className="action-btn more">
                                            <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="course-stats">
                                    <p>
                                        <i className="fas fa-users"></i>
                                        {course.students} Students
                                    </p>
                                    <div className="progress-container">
                                        <div className="progress-bar">
                                            <div 
                                                className="progress" 
                                                style={{ width: `${course.progress}%` }}
                                            ></div>
                                        </div>
                                        <span className="progress-text">{course.progress}% Complete</span>
                                    </div>
                                </div>
                                <div className="course-footer">
                                    <span className={`status-badge ${course.status.toLowerCase()}`}>
                                        {course.status}
                                    </span>
                                    <button className="view-details-btn">
                                        View Details
                                        <i className="fas fa-arrow-right"></i>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'students' && (
                <div className="dashboard-section">
                    <div className="section-header">
                        <h2>Your Students</h2>
                        <div className="section-actions">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input 
                                    type="text" 
                                    placeholder="Search students..."
                                />
                            </div>
                            <div className="filter-dropdown">
                                <button className="filter-btn">
                                    <i className="fas fa-filter"></i>
                                    Filter
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="students-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Student Name</th>
                                    <th>Course</th>
                                    <th>Progress</th>
                                    <th>Last Active</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dashboardData.students.map(student => (
                                    <tr key={student.id}>
                                        <td>
                                            <div className="student-info">
                                                <img 
                                                    src={student.avatar || '/default-avatar.png'} 
                                                    alt={student.name}
                                                    className="student-avatar"
                                                />
                                                <span>{student.name}</span>
                                            </div>
                                        </td>
                                        <td>{student.course}</td>
                                        <td>
                                            <div className="progress-bar">
                                                <div 
                                                    className="progress" 
                                                    style={{ width: `${student.progress}%` }}
                                                ></div>
                                                <span>{student.progress}%</span>
                                            </div>
                                        </td>
                                        <td>{new Date(student.lastActive).toLocaleDateString()}</td>
                                        <td>
                                            <span className={`status-badge ${student.status.toLowerCase()}`}>
                                                {student.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="action-btn" title="View Details">
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                                <button className="action-btn" title="Message">
                                                    <i className="fas fa-comment"></i>
                                                </button>
                                                <button className="action-btn" title="More">
                                                    <i className="fas fa-ellipsis-v"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper function to get activity icon based on type
const getActivityIcon = (type) => {
    switch (type) {
        case 'course_created':
            return 'plus-circle';
        case 'course_updated':
            return 'edit';
        case 'student_enrolled':
            return 'user-plus';
        case 'course_completed':
            return 'check-circle';
        case 'feedback_received':
            return 'star';
        case 'announcement_posted':
            return 'bullhorn';
        default:
            return 'bell';
    }
};

export default TrainerDashboard; 